import { txt } from './i18n';

// Tipologia del panel A del featured graphic: cruza la accesibilidad retenida
// (indice E2SFCA con alfa 0.5) contra el peso de la poblacion trabajadora, la
// fraccion de la poblacion diurna de la celda que llega a trabajar. El peso lo
// calcula preparar_datos.py (suavizado con kernel, como el panel C) y viaja en
// grilla.json. Reproduce los cortes y los colores de 07-figura.py para que la
// app clasifique cada celda igual que la figura publicada. Los nombres se
// traducen en textos.js; aqui solo viven sus claves.

// Cortes del panel A (07-figura.py: BINS_PESO y BINS_ACCESIBILIDAD).
const CORTES_PESO = [0.4, 0.6]; // <0.4 bajo, [0.4,0.6) medio, >=0.6 alto
const CORTES_ACC = [1 / 3, 2 / 3]; // <1/3 baja, [1/3,2/3) media, >=2/3 alta

function clase(valor, cortes) {
  let c = 0;
  for (const corte of cortes) {
    if (valor >= corte) c += 1;
  }
  return c;
}

// Paleta bivariada de la figura (chiricoca BIVARIATE_BLUE_ORANGE), indexada
// [claseAcc][clasePeso]. Fila 0 = accesibilidad baja; columna 0 = peso bajo.
// Azul = residencial bien servido, naranja = distrito monofuncional, vino
// oscuro = centro de uso mixto.
export const MATRIZ_COLORES = [
  ['#eeeeee', '#f0c3a0', '#da9768'], // accesibilidad baja
  ['#b6bfec', '#bf96a0', '#af6d6c'], // accesibilidad media
  ['#6d8bc9', '#816481', '#6e384b'], // accesibilidad alta
];

// Clave del nombre por celda de la matriz [claseAcc][clasePeso]. Las cuatro
// esquinas llevan los terminos de la figura; las intermedias se describen por
// su posicion.
const CLAVES_NOMBRE = [
  ['tipo.desatendida', 'tipo.desatendida', 'tipo.monofuncional'],
  ['tipo.media', 'tipo.media', 'tipo.mediaAltoPeso'],
  ['tipo.residencial', 'tipo.residencial', 'tipo.mixto'],
];

// La paleta de arriba esta hecha para el papel blanco de la figura, donde el
// gris claro (poca accesibilidad, pocos trabajadores) se hunde en el fondo y
// el vino oscuro salta. Sobre el basemap negro pasa lo contrario, asi que la
// app adapta la misma paleta en vez de cambiarla: los tonos no se tocan, se
// aclara cada color en proporcion a lo oscuro que sea (para que el vino no se
// hunda en el negro) y se baja la opacidad de la esquina sin informacion (las
// celdas desatendidas), que es lo que en la figura hace el fondo blanco.
//
// Esta conversion es la unica fuente de color de la tipologia en la app: la
// usan la capa del mapa, la matriz de la leyenda y el panel de la celda, para
// que la clave y el mapa muestren exactamente el mismo color.
const ACLARADO_MAX = 0.3;
const OPACIDAD_POR_NIVEL = [65, 170, 235];

function componentes(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function aclara(rgb) {
  const luma = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
  const f = ACLARADO_MAX * (1 - luma);
  return rgb.map((c) => Math.round(c + (255 - c) * f));
}

// [r, g, b, a] por clase, para la capa de deck.gl (que no lee hex)
export const MATRIZ_RGBA = MATRIZ_COLORES.map((fila, acc) =>
  fila.map((hex, peso) => [
    ...aclara(componentes(hex)),
    OPACIDAD_POR_NIVEL[Math.max(acc, peso)],
  ])
);

// Las mismas nueve, como color CSS, para la matriz y las muestras del HUD
export const MATRIZ_CSS = MATRIZ_RGBA.map((fila) =>
  fila.map(([r, g, b, a]) => `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(2)})`)
);

// Version opaca, para texto sobre el fondo del panel (el color con opacidad
// baja no se lee como tipografia)
const MATRIZ_TEXTO = MATRIZ_COLORES.map((fila) =>
  fila.map((hex) => {
    const [r, g, b] = aclara(componentes(hex));
    return `rgb(${r}, ${g}, ${b})`;
  })
);

// Cortes de la matriz, para rotular los ejes de la leyenda. Van de menor a
// mayor e incluyen los extremos.
export const CORTES_EJE_X = ['0', '0.4', '0.6', '1'];
export const CORTES_EJE_Y = ['0', '0.33', '0.67', '1'];

export const NIVELES_PESO = ['bajo', 'medio', 'alto'];
export const NIVELES_ACC = ['baja', 'media', 'alta'];

// Las cuatro esquinas, para la leyenda del panel de tipologia.
export const ESQUINAS = [
  { accClass: 2, pesoClass: 2, nombre: txt('tipo.mixto') },
  { accClass: 2, pesoClass: 0, nombre: txt('tipo.residencial') },
  { accClass: 0, pesoClass: 2, nombre: txt('tipo.monofuncional') },
  { accClass: 0, pesoClass: 0, nombre: txt('tipo.desatendida') },
];

export function clasificaTipologia(acc50, peso) {
  const retenida = acc50;
  const pesoSeguro = peso ?? 0;
  const accClass = clase(retenida, CORTES_ACC);
  const pesoClass = clase(pesoSeguro, CORTES_PESO);
  return {
    acc50,
    retenida,
    peso: pesoSeguro,
    accClass,
    pesoClass,
    nombre: txt(CLAVES_NOMBRE[accClass][pesoClass]),
    color: MATRIZ_TEXTO[accClass][pesoClass],
  };
}
